// Preset routines available in the timer dropdown.
// Each subroutine has: name, duration (seconds, or null for open-ended), delay (seconds before it starts).
// Open-ended subroutines also accept an `interval` (seconds) for periodic bell alerts.

export const PRESET_ROUTINES = {
  meditation: [
    {
      name: '10 Min Mindfulness',
      description: 'A short mindfulness session to settle the mind and bring gentle focus to the present moment.',
      subroutines: [
        { name: 'Opening', duration: 1 * 60, delay: 0 },
        { name: 'Body Scan', duration: 5 * 60, delay: 5 },
        { name: 'Closing', duration: 4 * 60, delay: 5 },
      ],
    },
    {
      name: '15 Min Guided Visualization',
      description: 'A guided visualization routine designed to deepen relaxation and mental clarity.',
      subroutines: [
        { name: 'Setup', duration: 2 * 60, delay: 0 },
        { name: 'Visualization', duration: 11 * 60, delay: 5 },
        { name: 'Return', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: '20 Min Body Scan',
      description: 'A calming body scan meditation that helps release tension and increase body awareness.',
      subroutines: [
        { name: 'Opening', duration: 2 * 60, delay: 0 },
        { name: 'Body Scan', duration: 16 * 60, delay: 5 },
        { name: 'Integration', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: '30 Min Deep Meditation',
      description: 'A longer meditation for deep focus, stillness, and expanded awareness.',
      subroutines: [
        { name: 'Preparation', duration: 3 * 60, delay: 0 },
        { name: 'Deep Meditation', duration: 24 * 60, delay: 5 },
        { name: 'Gentle Return', duration: 3 * 60, delay: 5 },
      ],
    },
    {
      name: 'Afternoon Reset',
      description: 'A quick reset to refresh energy and mental balance during the afternoon.',
      subroutines: [
        { name: 'Transition', duration: 1 * 60, delay: 0 },
        { name: 'Clarity Work', duration: 6 * 60, delay: 5 },
        { name: 'Mental Reset', duration: 3 * 60, delay: 5 },
      ],
    },
    {
      name: 'Breath Awareness',
      description: 'A breathing-centered practice for steady focus and calm energy.',
      subroutines: [
        { name: 'Settling', duration: 2 * 60, delay: 0 },
        { name: 'Breath Counting', duration: 12 * 60, delay: 5 },
        { name: 'Natural Breath', duration: 4 * 60, delay: 5 },
        { name: 'Closing', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: 'Chakra Meditation',
      description: 'A centering meditation that brings attention to energy centers for balance and grounding.',
      subroutines: [
        { name: 'Centering', duration: 2 * 60, delay: 0 },
        { name: 'Chakra Focus', duration: 18 * 60, delay: 5 },
        { name: 'Integration', duration: 3 * 60, delay: 5 },
      ],
    },
    {
      name: 'Loving-Kindness (Metta)',
      description: 'A compassionate practice focused on cultivating kindness toward yourself and others.',
      subroutines: [
        { name: 'Setup', duration: 3 * 60, delay: 0 },
        { name: 'Self-Compassion', duration: 6 * 60, delay: 5 },
        { name: 'Loving-Kindness', duration: 10 * 60, delay: 5 },
        { name: 'Closing', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: 'Mantra Repetition',
      description: 'A practice using repeated mantras to stabilize the mind and deepen concentration.',
      subroutines: [
        { name: 'Opening', duration: 2 * 60, delay: 0 },
        { name: 'Mantra Focus', duration: 12 * 60, delay: 5 },
        { name: 'Silent Hold', duration: 4 * 60, delay: 5 },
        { name: 'Release', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: 'Morning Energizer',
      description: 'A morning meditation to build positive energy and prepare you for the day ahead.',
      subroutines: [
        { name: 'Awakening', duration: 2 * 60, delay: 0 },
        { name: 'Energy Building', duration: 8 * 60, delay: 5 },
        { name: 'Vitality Focus', duration: 5 * 60, delay: 5 },
      ],
    },
    {
      name: 'Nature Connection',
      description: 'A visualization journey to connect with nature and restore a sense of calm.',
      subroutines: [
        { name: 'Setup', duration: 2 * 60, delay: 0 },
        { name: 'Nature Visualization', duration: 13 * 60, delay: 5 },
        { name: 'Gratitude', duration: 5 * 60, delay: 5 },
      ],
    },
    {
      name: 'Progressive Relaxation',
      description: 'A deliberate relaxation sequence to release tension from head to toe.',
      subroutines: [
        { name: 'Start', duration: 2 * 60, delay: 0 },
        { name: 'Muscle Groups', duration: 14 * 60, delay: 5 },
        { name: 'Complete Relaxation', duration: 4 * 60, delay: 5 },
      ],
    },
    {
      name: 'Quick 5 Min Breathing',
      description: 'A brief breathing practice for fast calm and mental reset.',
      subroutines: [
        { name: 'Centering', duration: 1 * 60, delay: 0 },
        { name: 'Breath Work', duration: 4 * 60, delay: 5 },
      ],
    },
    {
      name: 'Shambhavi Mahamudra',
      description: 'A traditional practice combining kriya, breath, and meditation for energetic alignment.',
      subroutines: [
        { name: 'Sukha Kriya', duration: 5 * 60, delay: 0 },
        { name: 'AUM Chanting', duration: 5 * 60, delay: 0 },
        { name: 'Viparita Swasa', duration: 5 * 60, delay: 0 },
        { name: 'Bandas', duration: 30, delay: 0 },
        { name: 'Meditation', duration: null, delay: 0, interval: 5 * 60 },
      ],
    },
    {
      name: 'Sleep Preparation',
      description: 'A gentle sequence to quiet the mind and prepare the body for restful sleep.',
      subroutines: [
        { name: 'Relaxation', duration: 5 * 60, delay: 0 },
        { name: 'Guided Relaxation', duration: 15 * 60, delay: 5 },
        { name: 'Rest', duration: null, delay: 5, interval: 10 * 60 },
      ],
    },
    {
      name: 'Sound Bathing',
      description: 'A sound-focused session for deep relaxation and soothing inner calm.',
      subroutines: [
        { name: 'Preparation', duration: 2 * 60, delay: 0 },
        { name: 'Sound Journey', duration: null, delay: 5, interval: 6 * 60 },
      ],
    },
    {
      name: 'Stress Relief (10 min)',
      description: 'A short stress relief routine to let go of tension and restore peace.',
      subroutines: [
        { name: 'Grounding', duration: 2 * 60, delay: 0 },
        { name: 'Release', duration: 6 * 60, delay: 5 },
        { name: 'Peace', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: 'Walking Meditation',
      description: 'A mindful walking practice that brings awareness to movement and breath.',
      subroutines: [
        { name: 'Centering', duration: 2 * 60, delay: 0 },
        { name: 'Slow Walking', duration: 13 * 60, delay: 5 },
        { name: 'Stillness', duration: 5 * 60, delay: 5 },
      ],
    },
    {
      name: 'Zen Sitting (Zazen)',
      description: 'A simple seated meditation focused on stillness, posture, and presence.',
      subroutines: [
        { name: 'Positioning', duration: 1 * 60, delay: 0 },
        { name: 'Silent Sitting', duration: null, delay: 5, interval: 10 * 60 },
      ],
    },
  ],
  workout: [
    {
      name: 'Back & Biceps',
      description: 'A strength routine focused on back and biceps work with compound lifts and targeted arm training.',
      subroutines: [
        { name: 'Warm Up', duration: 3 * 60, delay: 0 },
        { name: 'Rows (5 sets)', duration: 15 * 60, delay: 5 },
        { name: 'Rest', duration: 2 * 60, delay: 5 },
        { name: 'Pull Ups (4 sets)', duration: 12 * 60, delay: 5 },
        { name: 'Rest', duration: 2 * 60, delay: 5 },
        { name: 'Bicep Curls (4 sets)', duration: 10 * 60, delay: 5 },
      ],
    },
    {
      name: 'Bench Focus',
      description: 'A chest-centered workout designed to build pressing strength and upper body stability.',
      subroutines: [
        { name: 'Warm Up', duration: 5 * 60, delay: 0 },
        { name: 'Bench Press (6 sets)', duration: 20 * 60, delay: 5 },
        { name: 'Rest', duration: 3 * 60, delay: 5 },
        { name: 'Incline Press (4 sets)', duration: 12 * 60, delay: 5 },
        { name: 'Accessory Work', duration: 8 * 60, delay: 5 },
      ],
    },
    {
      name: 'Bodyweight Only',
      description: 'A bodyweight routine that builds strength using pushups, pullups, squats, and dips without equipment.',
      subroutines: [
        { name: 'Warm Up', duration: 2 * 60, delay: 0 },
        { name: 'Push Ups (5 sets)', duration: 10 * 60, delay: 5 },
        { name: 'Pull Ups (5 sets)', duration: 10 * 60, delay: 5 },
        { name: 'Squats (5 sets)', duration: 8 * 60, delay: 5 },
        { name: 'Dips (4 sets)', duration: 8 * 60, delay: 5 },
      ],
    },
    {
      name: 'Chest & Triceps',
      description: 'A focused upper-body session for building chest strength and tricep endurance.',
      subroutines: [
        { name: 'Warm Up', duration: 3 * 60, delay: 0 },
        { name: 'Bench Press (5 sets)', duration: 15 * 60, delay: 5 },
        { name: 'Rest', duration: 2 * 60, delay: 5 },
        { name: 'Incline Bench (4 sets)', duration: 12 * 60, delay: 5 },
        { name: 'Rest', duration: 1 * 60, delay: 5 },
        { name: 'Tricep Work (4 sets)', duration: 10 * 60, delay: 5 },
      ],
    },
    {
      name: 'Core Workout',
      description: 'A core-strengthening routine to improve stability, posture, and midsection endurance.',
      subroutines: [
        { name: 'Warm Up', duration: 2 * 60, delay: 0 },
        { name: 'Planks', duration: 4 * 60, delay: 5 },
        { name: 'Crunches', duration: 4 * 60, delay: 5 },
        { name: 'Leg Raises', duration: 4 * 60, delay: 5 },
        { name: 'Russian Twists', duration: 4 * 60, delay: 5 },
        { name: 'Mountain Climbers', duration: 4 * 60, delay: 5 },
      ],
    },
    {
      name: 'Deadlift Focus',
      description: 'A deadlift session with warm-up and accessory work for lower back and posterior chain strength.',
      subroutines: [
        { name: 'Warm Up', duration: 5 * 60, delay: 0 },
        { name: 'Deadlifts (5 sets)', duration: 20 * 60, delay: 5 },
        { name: 'Rest', duration: 3 * 60, delay: 5 },
        { name: 'Accessory Work', duration: 10 * 60, delay: 5 },
      ],
    },
    {
      name: 'Dumbbell Workout',
      description: 'A dumbbell-based full-body session for strength and muscular balance.',
      subroutines: [
        { name: 'Warm Up', duration: 2 * 60, delay: 0 },
        { name: 'Dumbbell Press (5 sets)', duration: 10 * 60, delay: 5 },
        { name: 'Dumbbell Rows (5 sets)', duration: 10 * 60, delay: 5 },
        { name: 'Shoulder Work (4 sets)', duration: 8 * 60, delay: 5 },
        { name: 'Curls & Extensions', duration: 8 * 60, delay: 5 },
      ],
    },
    {
      name: 'Full Body Strength',
      description: 'A compound lift routine for full-body strength and movement efficiency.',
      subroutines: [
        { name: 'Warm Up', duration: 3 * 60, delay: 0 },
        { name: 'Compound Move 1', duration: 10 * 60, delay: 5 },
        { name: 'Compound Move 2', duration: 10 * 60, delay: 5 },
        { name: 'Compound Move 3', duration: 10 * 60, delay: 5 },
        { name: 'Accessory Work', duration: 8 * 60, delay: 5 },
      ],
    },
    {
      name: 'Full Body HIIT',
      description: 'A high-intensity interval session for cardiovascular conditioning and fat-burning power.',
      subroutines: [
        { name: 'Warm Up', duration: 2 * 60, delay: 0 },
        { name: 'Burpees', duration: 40, delay: 5 },
        { name: 'Rest', duration: 20, delay: 5 },
        { name: 'Mountain Climbers', duration: 40, delay: 5 },
        { name: 'Rest', duration: 20, delay: 5 },
        { name: 'Jump Squats', duration: 40, delay: 5 },
        { name: 'Rest', duration: 20, delay: 5 },
        { name: 'Push Ups', duration: 40, delay: 5 },
        { name: 'Rest', duration: 20, delay: 5 },
        { name: 'Repeat (3 rounds)', duration: null, delay: 5, interval: 3 * 60 },
      ],
    },
    {
      name: 'Kettlebell Workout',
      description: 'A kettlebell circuit for strength, coordination, and functional conditioning.',
      subroutines: [
        { name: 'Warm Up', duration: 2 * 60, delay: 0 },
        { name: 'Kettlebell Swings (5 sets)', duration: 10 * 60, delay: 5 },
        { name: 'Turkish Get Ups', duration: 8 * 60, delay: 5 },
        { name: 'Goblet Squats', duration: 8 * 60, delay: 5 },
        { name: 'Kettlebell Rows', duration: 8 * 60, delay: 5 },
      ],
    },
    {
      name: 'Leg Day',
      description: 'A lower-body workout focused on squats, presses, and hamstring strengthening.',
      subroutines: [
        { name: 'Warm Up', duration: 3 * 60, delay: 0 },
        { name: 'Squats (5 sets)', duration: 15 * 60, delay: 5 },
        { name: 'Rest', duration: 2 * 60, delay: 5 },
        { name: 'Leg Press (4 sets)', duration: 12 * 60, delay: 5 },
        { name: 'Rest', duration: 2 * 60, delay: 5 },
        { name: 'Leg Curls (4 sets)', duration: 10 * 60, delay: 5 },
      ],
    },
    {
      name: 'Lower Body Strength',
      description: 'A strength routine emphasizing the lower body with heavy lifts and accessory work.',
      subroutines: [
        { name: 'Warm Up', duration: 3 * 60, delay: 0 },
        { name: 'Primary Lift', duration: 12 * 60, delay: 5 },
        { name: 'Rest', duration: 2 * 60, delay: 5 },
        { name: 'Secondary Work', duration: 10 * 60, delay: 5 },
        { name: 'Accessory', duration: 8 * 60, delay: 5 },
      ],
    },
    {
      name: 'Pull Day',
      description: 'A pulling-focused session targeting back, biceps, and grip strength.',
      subroutines: [
        { name: 'Warm Up', duration: 3 * 60, delay: 0 },
        { name: 'Pull Ups (5 sets)', duration: 12 * 60, delay: 5 },
        { name: 'Barbell Rows (5 sets)', duration: 15 * 60, delay: 5 },
        { name: 'Rest', duration: 2 * 60, delay: 5 },
        { name: 'Accessory Work', duration: 10 * 60, delay: 5 },
      ],
    },
    {
      name: 'Push Day',
      description: 'A push-focused workout for chest, shoulders, and triceps development.',
      subroutines: [
        { name: 'Warm Up', duration: 3 * 60, delay: 0 },
        { name: 'Bench Press (5 sets)', duration: 15 * 60, delay: 5 },
        { name: 'Overhead Press (4 sets)', duration: 12 * 60, delay: 5 },
        { name: 'Rest', duration: 2 * 60, delay: 5 },
        { name: 'Accessory Work', duration: 10 * 60, delay: 5 },
      ],
    },
    {
      name: 'Quick 15 Min Workout',
      description: 'A condensed, fast-paced workout designed for efficiency when you have only 15 minutes.',
      subroutines: [
        { name: 'Warm Up', duration: 2 * 60, delay: 0 },
        { name: 'Jumping Jacks', duration: 30, delay: 5 },
        { name: 'Push Ups', duration: 60, delay: 5 },
        { name: 'Lunges', duration: 60, delay: 5 },
        { name: 'Plank', duration: 45, delay: 5 },
        { name: 'Repeat x3', duration: null, delay: 5, interval: 2 * 60 },
        { name: 'Cool Down', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: 'Running / Cardio',
      description: 'A cardio session combining easy running, tempo effort, and recovery walking.',
      subroutines: [
        { name: 'Warm Up Walk', duration: 3 * 60, delay: 0 },
        { name: 'Easy Run', duration: 15 * 60, delay: 5 },
        { name: 'Tempo Run', duration: 5 * 60, delay: 5 },
        { name: 'Sprint Intervals', duration: 2 * 60, delay: 5 },
        { name: 'Cool Down Walk', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: 'Shoulder Focus',
      description: 'A shoulder workout for pressing strength and balanced shoulder development.',
      subroutines: [
        { name: 'Warm Up', duration: 2 * 60, delay: 0 },
        { name: 'Overhead Press (5 sets)', duration: 15 * 60, delay: 5 },
        { name: 'Rest', duration: 2 * 60, delay: 5 },
        { name: 'Lateral Raises (4 sets)', duration: 10 * 60, delay: 5 },
        { name: 'Face Pulls (3 sets)', duration: 8 * 60, delay: 5 },
      ],
    },
    {
      name: 'Squat Focus',
      description: 'A squat-centered session to build lower-body power and technique.',
      subroutines: [
        { name: 'Warm Up', duration: 5 * 60, delay: 0 },
        { name: 'Squats (6 sets)', duration: 20 * 60, delay: 5 },
        { name: 'Rest', duration: 3 * 60, delay: 5 },
        { name: 'Leg Work', duration: 8 * 60, delay: 5 },
      ],
    },
    {
      name: 'Strength Training',
      description: 'A classic strength session with compound lifts for overall muscular development.',
      subroutines: [
        { name: 'Warm Up', duration: 3 * 60, delay: 0 },
        { name: 'Bench Press (5 sets)', duration: 15 * 60, delay: 5 },
        { name: 'Rest', duration: 2 * 60, delay: 5 },
        { name: 'Barbell Rows (5 sets)', duration: 15 * 60, delay: 5 },
        { name: 'Rest', duration: 2 * 60, delay: 5 },
        { name: 'Squats (4 sets)', duration: 12 * 60, delay: 5 },
      ],
    },
    {
      name: 'Upper Body Strength',
      description: 'An upper-body strength routine combining push and pull movements into one session.',
      subroutines: [
        { name: 'Warm Up', duration: 2 * 60, delay: 0 },
        { name: 'Primary Push', duration: 12 * 60, delay: 5 },
        { name: 'Primary Pull', duration: 12 * 60, delay: 5 },
        { name: 'Rest', duration: 2 * 60, delay: 5 },
        { name: 'Accessory', duration: 8 * 60, delay: 5 },
      ],
    },
  ],
  flexibility: [
    {
      name: 'Beginner Yoga',
      description: 'A gentle yoga flow for beginners, mixing breathing, standing poses, and relaxation.',
      subroutines: [
        { name: 'Centering & Breathing', duration: 3 * 60, delay: 0 },
        { name: 'Sun Salutations', duration: 5 * 60, delay: 5 },
        { name: 'Standing Poses', duration: 10 * 60, delay: 5 },
        { name: 'Forward Folds', duration: 5 * 60, delay: 5 },
        { name: 'Child\'s Pose & Savasana', duration: 5 * 60, delay: 5 },
      ],
    },
    {
      name: 'Deep Hip Opener',
      description: 'A hip-opening sequence to improve flexibility and release tension in the lower body.',
      subroutines: [
        { name: 'Opening', duration: 2 * 60, delay: 0 },
        { name: 'Hip Circles', duration: 3 * 60, delay: 5 },
        { name: 'Pigeon Pose', duration: 4 * 60, delay: 5 },
        { name: 'Butterfly Stretch', duration: 4 * 60, delay: 5 },
        { name: 'Closing', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: 'Dynamic Stretching',
      description: 'A movement-based stretching routine to warm up the body and improve mobility.',
      subroutines: [
        { name: 'Leg Swings', duration: 2 * 60, delay: 0 },
        { name: 'Arm Circles', duration: 90, delay: 5 },
        { name: 'Walking Lunges', duration: 2 * 60, delay: 5 },
        { name: 'Inchworms', duration: 90, delay: 5 },
        { name: 'Torso Twists', duration: 2 * 60, delay: 5 },
        { name: 'Hip Circles', duration: 90, delay: 5 },
      ],
    },
    {
      name: 'Evening Flexibility',
      description: 'A calming flexibility routine designed to loosen muscles and promote evening relaxation.',
      subroutines: [
        { name: 'Calm Setup', duration: 2 * 60, delay: 0 },
        { name: 'Gentle Stretches', duration: 8 * 60, delay: 5 },
        { name: 'Restorative Hold', duration: 8 * 60, delay: 5 },
        { name: 'Relaxation', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: 'Foam Rolling Recovery',
      description: 'A guided foam rolling session to relieve tightness and speed recovery.',
      subroutines: [
        { name: 'Start', duration: 1 * 60, delay: 0 },
        { name: 'Legs', duration: 5 * 60, delay: 5 },
        { name: 'Back & Glutes', duration: 5 * 60, delay: 5 },
        { name: 'Shoulders', duration: 4 * 60, delay: 5 },
        { name: 'Cool Down', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: 'Full Body Stretch',
      description: 'A full-body stretch sequence for improved mobility and overall muscle relaxation.',
      subroutines: [
        { name: 'Neck Rolls', duration: 60, delay: 0 },
        { name: 'Shoulder Stretch', duration: 2 * 60, delay: 5 },
        { name: 'Chest & Bicep Stretch', duration: 2 * 60, delay: 5 },
        { name: 'Spinal Twist', duration: 2 * 60, delay: 5 },
        { name: 'Hamstring Stretch', duration: 2 * 60, delay: 5 },
        { name: 'Quad Stretch', duration: 2 * 60, delay: 5 },
        { name: 'Hip Flexor Stretch', duration: 2 * 60, delay: 5 },
        { name: 'Calf Stretch', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: 'Hamstring Focus',
      description: 'A targeted hamstring stretch routine to increase flexibility and relieve tension.',
      subroutines: [
        { name: 'Setup', duration: 1 * 60, delay: 0 },
        { name: 'Active Stretching', duration: 4 * 60, delay: 5 },
        { name: 'Deep Holds', duration: 8 * 60, delay: 5 },
        { name: 'Release', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: 'Lower Back Release',
      description: 'A lower back mobility routine to ease stiffness and support spinal comfort.',
      subroutines: [
        { name: 'Opening', duration: 2 * 60, delay: 0 },
        { name: 'Spinal Twists', duration: 4 * 60, delay: 5 },
        { name: 'Cat-Cow', duration: 4 * 60, delay: 5 },
        { name: 'Child\'s Pose', duration: 4 * 60, delay: 5 },
        { name: 'Relaxation', duration: 1 * 60, delay: 5 },
      ],
    },
    {
      name: 'Morning Flexibility',
      description: 'A morning movement routine to wake up the body and improve flexibility.',
      subroutines: [
        { name: 'Wake Up', duration: 2 * 60, delay: 0 },
        { name: 'Gentle Stretches', duration: 8 * 60, delay: 5 },
        { name: 'Dynamic Movement', duration: 5 * 60, delay: 5 },
      ],
    },
    {
      name: 'Office Desk Stretches',
      description: 'A quick desk-friendly stretch routine to release tension from sitting.',
      subroutines: [
        { name: 'Neck Release', duration: 2 * 60, delay: 0 },
        { name: 'Shoulder Rolls', duration: 2 * 60, delay: 5 },
        { name: 'Wrist Circles', duration: 1 * 60, delay: 5 },
        { name: 'Seated Twists', duration: 2 * 60, delay: 5 },
        { name: 'Standing Stretch', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: 'Pilates Core',
      description: 'A pilates-inspired session for core engagement, posture, and controlled movement.',
      subroutines: [
        { name: 'Setup', duration: 2 * 60, delay: 0 },
        { name: 'Core Engagement', duration: 5 * 60, delay: 5 },
        { name: 'Stretches', duration: 5 * 60, delay: 5 },
        { name: 'Relaxation', duration: 3 * 60, delay: 5 },
      ],
    },
    {
      name: 'Post-Run Flexibility',
      description: 'A post-run stretch routine to loosen tight muscles and aid recovery.',
      subroutines: [
        { name: 'Cool Down', duration: 2 * 60, delay: 0 },
        { name: 'Lower Body Focus', duration: 6 * 60, delay: 5 },
        { name: 'Upper Body', duration: 4 * 60, delay: 5 },
        { name: 'Core Stretch', duration: 3 * 60, delay: 5 },
      ],
    },
    {
      name: 'Post-Workout Stretch',
      description: 'A cooldown stretching sequence to restore flexibility after exercise.',
      subroutines: [
        { name: 'Cool Down Breathing', duration: 2 * 60, delay: 0 },
        { name: 'Quad Stretch', duration: 90, delay: 5 },
        { name: 'Hamstring Stretch', duration: 90, delay: 5 },
        { name: 'Glute Stretch', duration: 90, delay: 5 },
        { name: 'Chest Stretch', duration: 90, delay: 5 },
        { name: 'Shoulder Stretch', duration: 90, delay: 5 },
        { name: 'Standing Forward Fold', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: 'Restorative Yoga',
      description: 'A restorative yoga session for relaxation, gentle support, and deep calm.',
      subroutines: [
        { name: 'Opening', duration: 2 * 60, delay: 0 },
        { name: 'Supported Poses', duration: 12 * 60, delay: 5 },
        { name: 'Deep Relaxation', duration: 6 * 60, delay: 5 },
      ],
    },
    {
      name: 'Shoulder Mobility',
      description: 'A shoulder mobility routine designed to improve joint range and reduce stiffness.',
      subroutines: [
        { name: 'Activation', duration: 2 * 60, delay: 0 },
        { name: 'Arm Circles', duration: 3 * 60, delay: 5 },
        { name: 'Deep Stretches', duration: 5 * 60, delay: 5 },
        { name: 'Release', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: 'Spine Mobility',
      description: 'A spinal mobility flow to increase movement and support healthy posture.',
      subroutines: [
        { name: 'Centering', duration: 2 * 60, delay: 0 },
        { name: 'Spinal Twists', duration: 4 * 60, delay: 5 },
        { name: 'Extensions', duration: 4 * 60, delay: 5 },
        { name: 'Lateral Flexion', duration: 3 * 60, delay: 5 },
        { name: 'Integration', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: 'Upper Body Flexibility',
      description: 'An upper-body flexibility routine to open the chest, shoulders, and arms.',
      subroutines: [
        { name: 'Opening', duration: 2 * 60, delay: 0 },
        { name: 'Shoulder Stretch', duration: 4 * 60, delay: 5 },
        { name: 'Chest Opening', duration: 4 * 60, delay: 5 },
        { name: 'Arm & Wrist', duration: 3 * 60, delay: 5 },
        { name: 'Relaxation', duration: 2 * 60, delay: 5 },
      ],
    },
    {
      name: 'Vinyasa Yoga Flow',
      description: 'A flowing yoga sequence that combines movement, breath, and relaxation.',
      subroutines: [
        { name: 'Opening & Pranayama', duration: 5 * 60, delay: 0 },
        { name: 'Sun Salutations (A)', duration: 8 * 60, delay: 5 },
        { name: 'Sun Salutations (B)', duration: 8 * 60, delay: 5 },
        { name: 'Standing Sequence', duration: 12 * 60, delay: 5 },
        { name: 'Floor Sequence', duration: 10 * 60, delay: 5 },
        { name: 'Savasana', duration: 7 * 60, delay: 5 },
      ],
    },
    {
      name: 'Wrist & Hand Stretches',
      description: 'A focused stretch session for wrists, hands, and forearms to ease tension.',
      subroutines: [
        { name: 'Opening', duration: 1 * 60, delay: 0 },
        { name: 'Wrist Circles', duration: 2 * 60, delay: 5 },
        { name: 'Finger Stretches', duration: 3 * 60, delay: 5 },
        { name: 'Forearm Stretches', duration: 3 * 60, delay: 5 },
        { name: 'Closing', duration: 1 * 60, delay: 5 },
      ],
    },
    {
      name: 'Yin Yoga',
      description: 'A slow, long-hold yoga practice for deep release and restful recovery.',
      subroutines: [
        { name: 'Opening', duration: 3 * 60, delay: 0 },
        { name: 'Long Holds', duration: 18 * 60, delay: 5 },
        { name: 'Savasana', duration: 5 * 60, delay: 5 },
      ],
    },
  ],
  generic: [
    {
      name: 'Bell every 30 sec',
      description: 'A timer preset that rings every 30 seconds for pacing short intervals or focus checks.',
      subroutines: [
        { name: 'Open Timer', duration: null, delay: 0, interval: 30 },
      ],
    },
    {
      name: 'Bell every 1 min',
      description: 'A timer preset that sounds every minute for consistent short-interval work.',
      subroutines: [
        { name: 'Open Timer', duration: null, delay: 0, interval: 1 * 60 },
      ],
    },
    {
      name: 'Bell every 2 min',
      description: 'A timer preset for regular 2-minute interval reminders.',
      subroutines: [
        { name: 'Open Timer', duration: null, delay: 0, interval: 2 * 60 },
      ],
    },
    {
      name: 'Bell every 3 min',
      description: 'A timer preset to mark every 3 minutes with a bell.',
      subroutines: [
        { name: 'Open Timer', duration: null, delay: 0, interval: 3 * 60 },
      ],
    },
    {
      name: 'Bell every 5 min',
      description: 'A timer preset for periodic 5-minute alerts.',
      subroutines: [
        { name: 'Open Timer', duration: null, delay: 0, interval: 5 * 60 },
      ],
    },
    {
      name: 'Bell every 10 min',
      description: 'A timer preset for longer 10-minute interval checks.',
      subroutines: [
        { name: 'Open Timer', duration: null, delay: 0, interval: 10 * 60 },
      ],
    },
    {
      name: 'Bell every 15 min',
      description: 'A timer preset for 15-minute reminders during open-ended work.',
      subroutines: [
        { name: 'Open Timer', duration: null, delay: 0, interval: 15 * 60 },
      ],
    },
    {
      name: 'Bell every 30 min',
      description: 'A timer preset for half-hourly bell alerts during long sessions.',
      subroutines: [
        { name: 'Open Timer', duration: null, delay: 0, interval: 30 * 60 },
      ],
    },
  ],
  breathing: [
    { name: 'Box Breathing', description: '4-4-4-4 equal phases. Calms the nervous system and reduces stress.', inhale: 4, hold: 4, exhale: 4, hold2: 4 },
    { name: '4-7-8 Breathing', description: 'Inhale 4s, hold 7s, exhale 8s. Promotes deep relaxation and sleep.', inhale: 4, hold: 7, exhale: 8, hold2: 0 },
    { name: 'Coherent Breathing', description: '5s inhale, 5s exhale. Balances the nervous system and heart rate.', inhale: 5, hold: 0, exhale: 5, hold2: 0 },
    { name: 'Energizing Breath', description: '6s inhale, 2s hold, 4s exhale. Calm focus without drowsiness.', inhale: 6, hold: 2, exhale: 4, hold2: 0 },
  ],
};
